<template>
    <div class="md-layout">
        <div class="md-layout-item">
            <div v-if="!loadingState">
                <div v-if="isUpdateMode" class="md-layout md-gutter md-alignment-top-right">
                    <md-button class="md-primary" @click="settings.showDeleteModal = true"> {{$t('Delete')}}</md-button>
                </div>
                <div class="md-layout md-gutter">
                    <div class="md-layout-item md-small-size-100">
                        <md-field>
                            <label>{{ $t('Name') }}</label>
                            <md-input v-model="organization.name" type="text"></md-input>
                        </md-field>
                    </div>
                    <div class="md-layout-item md-small-size-100">
                        <md-field>
                            <label> {{ $t('Reseller') }} </label>
                            <md-select :disabled="this.isUpdateMode" v-model="organization.resellerId" name="resellers"
                                       id="resellers">
                                <md-option v-for="(reseller, index) in options.reseller" :key="index"
                                           v-bind:value="reseller.id">
                                    {{reseller.name}}
                                </md-option>
                            </md-select>
                        </md-field>
                    </div>
                </div>
                <div class="md-title form-title-margin">{{ $t('Address') }}</div>
                <div class="md-layout md-gutter">
                    <div class="md-layout-item md-small-size-100">
                        <md-field>
                            <label>{{ $t('Address line 1') }}</label>
                            <md-input v-model="organization.addressLine1" type="text"></md-input>
                        </md-field>
                        <md-field>
                            <label>{{ $t('Address line 2') }}</label>
                            <md-input v-model="organization.addressLine2" type="text"></md-input>
                        </md-field>
                        <md-field>
                            <label>{{ $t('Town') }}</label>
                            <md-input v-model="organization.town" type="text"></md-input>
                        </md-field>
                        <md-field>
                            <label>{{ $t('City') }}</label>
                            <md-input v-model="organization.city" type="text"></md-input>
                        </md-field>
                        <md-field>
                            <label>{{ $t('Region / County') }}</label>
                            <md-input v-model="organization.region" type="text"></md-input>
                        </md-field>
                        <md-field>
                            <label>{{ $t('Country') }}</label>
                            <md-input v-model="organization.country" type="text"></md-input>
                        </md-field>
                        <md-field>
                            <label>{{ $t('Postcode / Zip code') }}</label>
                            <md-input v-model="organization.postcode" type="text"></md-input>
                        </md-field>
                    </div>
                </div>
                <div v-if="selectedReseller">
                    <div class="md-title form-title-margin">{{ $t('Theme options') }}</div>
                    <md-switch v-model="organization.applyOrganizationTheme">{{ $t('Apply Organization Theme') }}</md-switch>
                </div>
                <div v-if="organization.applyOrganizationTheme">
                    <div class="md-layout-item md-small-size-100">
                        <div>
                            <label>{{ $t('Logo') }}</label>
                            <image-uploader
                                :value="organization.logoFile"
                                @targetChanged="onLogoTargetChanged"
                                @sourceChanged="onLogoSourceChanged"/>
                        </div>
                        <div>
                            <label>{{ $t('Icon upload') }}</label>
                            <image-uploader
                                :value="organization.iconFile"
                                @targetChanged="onIconTargetChanged"
                                @sourceChanged="onIconSourceChanged"/>
                        </div>
                    </div>
                    <md-field>
                        <label>{{ $t('Page footer') }}</label>
                        <md-textarea v-model="organization.pageFooter" type="text"></md-textarea>
                    </md-field>
                    <div class="md-title form-title-margin">{{ $t('Template') }}</div>
                    <div class="md-layout md-gutter">
                        <div class="md-layout-item md-small-size-100">
                            <color-picker v-model="organization.colorHeader">
                                <label>{{ $t('Header') }}</label>
                            </color-picker>
                        </div>
                        <div class="md-layout-item md-small-size-100">
                            <color-picker v-model="organization.colorMenu">
                                <label>{{ $t('Menu') }}</label>
                            </color-picker>
                        </div>
                        <div class="md-layout-item md-small-size-100">
                            <color-picker v-model="organization.colorFooter">
                                <label>{{ $t('Footer') }}</label>
                            </color-picker>
                        </div>
                    </div>
                    <div class="md-title form-title-margin"> {{ $t('Sidebar') }}</div>
                    <div class="md-layout md-gutter">
                        <div class="md-layout-item md-small-size-100">
                            <md-radio v-for="(button, index) in settings.radioButtons"
                                      v-model="organization.styleSidebar" :key="index" v-bind:value="button.id">
                                {{button.label}}
                            </md-radio>
                        </div>
                    </div>
                    <md-switch v-model="organization.styleLayoutBoxed">{{ $t('Boxed layout') }}</md-switch>
                    <md-switch v-model="organization.accordionModeMainMenu">{{ $t('Accordion mode') }}</md-switch>
                </div>
                <div class="md-title form-title-margin">{{ $t('Login page') }}</div>
                <md-switch v-model="organization.createLoginPage">{{ $t('Create custom login page') }}</md-switch>
                <div v-if="organization.createLoginPage">
                    <div>
                        <label>{{ $t('Login background page upload') }}</label>
                        <image-uploader
                            :value="organization.loginPageBackgroundFile"
                            @targetChanged="onLoginPageBackgroundTargetChanged"
                            @sourceChanged="onLoginPageBackgroundSourceChanged"/>
                    </div>
                    <md-field>
                        <label>{{ $t('Domain name link') }}</label>
                        <md-input v-model="organization.domainName" type="text"></md-input>
                    </md-field>
                </div>
                <div class="md-title form-title-margin">{{ $t('Site manager options') }}</div>
                <md-switch v-model="organization.smCanSwitchSites">{{ $t('Can switch sites within organization') }}</md-switch>
                <md-switch v-model="organization.dfsSmCanAccessFormsManager">{{ $t('DFS - Allow Site Manager form manager access') }}</md-switch>
                <md-switch v-model="organization.dfsBmCanAccessFormsManager">{{ $t('DFS - Allow Brand Manager form manager access') }}</md-switch>
                <md-switch v-model="organization.dfsSmCanAccessHaccpPolicy">{{ $t('DFS - HACCP Policy feature') }}</md-switch>
                <div class="md-title form-title-margin">{{ $t('Active') }}</div>
                <div>
                    <label>{{ $t('Enabled in Compliance') }}</label>
                    <div>
                        <md-radio v-model="organization.complianceActive" :value="'active'"> {{ $t('Active') }}</md-radio>
                        <md-radio v-model="organization.complianceActive" :value="'inactive'"> {{ $t('Inactive') }}</md-radio>
                        <md-radio v-model="organization.complianceActive" :value="'not_exist'"> {{ $t('Not Existing') }}</md-radio>
                    </div>
                </div>
                <div>
                    <label>Enabled in DFS</label>
                    <div>
                        <md-radio v-model="organization.dfsActive" :value="'active'">{{ $t('Active') }}</md-radio>
                        <md-radio v-model="organization.dfsActive" :value="'inactive'">{{ $t('Inactive') }}</md-radio>
                        <md-radio v-model="organization.dfsActive" :value="'not_exist'">{{ $t('Not Existing') }}</md-radio>
                    </div>
                </div>
                <div>
                    <label>Enabled in Academy</label>
                    <div>
                        <md-radio v-model="organization.academyActive" :value="'active'">{{ $t('Active') }}</md-radio>
                        <!--                    <md-radio v-model="organization.academyActive" :value="'inactive'">{{ $t('Inactive') }}</md-radio>-->
                        <md-radio v-model="organization.academyActive" :value="'not_exist'">{{ $t('Not Existing') }}</md-radio>
                    </div>
                </div>
                <div v-if="organization.dfsActive === 'active'">
                    <div class="md-title form-title-margin">{{ $t('Modules') }}</div>
                    <div class="md-layout md-gutter bottom-margin-30" v-if="modules.length > 0">
                        <div v-for="(module, index) in modules" :key="index"
                             class="md-layout-item md-small-size-100 md-medium-size-30 md-size-20">
                            <md-checkbox v-model="organization.modules" v-bind:value="module.id"> {{module.name}}
                            </md-checkbox>
                        </div>
                    </div>
                    <div class="md-subhead bottom-margin-30" v-else>
                        {{$t('Choose reseller first!') }}
                    </div>
                </div>
            </div>
            <loader v-else class="color"/>
        </div>
    </div>
</template>

<script>
    import AuthService from '../../services/AuthService';
    import ColorPicker from "../../components/Inputs/ColorPicker";
    import Loader from "../../../old/components/Loader";
    import debounce from "lodash/debounce"
    import ImageUploader from "@/new/components/ImageUploader";

    export default {
        model: {
            prop: 'organization',
            event: 'change'
        },
        name: "OrganizationTabGeneral",
        components: {ImageUploader, ColorPicker, Loader},
        props: {
            organization: {
                type: Object,
            },
            options: {
                type: Object
            },
            settings: {
                type: Object
            },
            isUpdateMode: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                modules: []
            }
        },
        computed: {
            loadingModules() {
                return this.modules === [];
            },
            selectedReseller() {
                return this.organization.resellerId
            },
            accessToken() {
                return AuthService.getAccessToken();
            },
            loadingState() {
                if (this.isUpdateMode) {
                    return this.loadingModules || this.settings.loadingOrganization;
                }
                return this.loadingModules;
            },
            pageTitle() {
                return this.isUpdateMode ? 'Edit Organization' : 'Create Organization';
            }
        },
        methods: {
            onLogoTargetChanged(event) {
                this.organization.logoFile = event;
            },
            onLogoSourceChanged(event) {
                this.organization.logoFile = event;
                this.organization.logo = 0;
            },
            onIconTargetChanged(event) {
                this.organization.iconFile = event;
            },
            onIconSourceChanged(event) {
                this.organization.iconFile = event;
                this.organization.icon = 0;
            },
            onLoginPageBackgroundTargetChanged(event) {
                this.organization.loginPageBackgroundFile = event;
            },
            onLoginPageBackgroundSourceChanged(event) {
                this.organization.loginPageBackgroundFile = event;
                this.organization.loginPageBackground = 0;
            },
            setResellerThemeData: debounce(function () {

                if (!this.options.applyOrganizationTheme) {

                    this.$api.resellerView({
                        segments: {
                            id: this.organization.resellerId,
                        }
                    }).then(({data}) => {
                        if (!this.isUpdateMode) {
                            this.organization.timeZoneId = data.model.time_zone_id ?? '';
                            this.organization.pageFooter = data.model.page_footer ?? '';
                            this.organization.colorHeader = data.model.color_header ?? '';
                            this.organization.colorMenu = data.model.color_menu ?? '';
                            this.organization.colorFooter = data.model.color_footer ?? '';
                            this.organization.accordionModeMainMenu = Boolean(data.model.accordion_mode_main_menu) ?? false;
                            this.organization.styleSidebar = data.model.style_sidebar ?? '';
                            this.organization.styleLayoutBoxed = Boolean(data.model.style_layout_boxed) ?? false;
                            if (this.organization.styleSidebar !== '') {
                                this.organization.styleSidebar[0].toUpperCase();
                            }
                            this.organization.icon = 0;
                            this.organization.logo = 0;
                            this.organization.logoFile = data.options.logo;
                            this.organization.iconFile = data.options.icon;
                            this.organization.modules =  data.model.modules;
                        }

                        var allModules = data.options.modules ?? [];
                        var selectedModules = data.model.modules ?? [];

                        this.modules  = allModules.filter(a => selectedModules.some(b => a.id === b));
                    }).catch(() => {
                        this.$router.back();
                    }).finally(() => {
                        this.settings.loadingOrganization = false;
                    });
                }
            }, 500),
            setModulesByReseller() {
                this.$api.getModulesByResellerId({
                    segments: {
                        id: this.organization.resellerId,
                    }
                }).then(({data}) => {
                    this.modules = data;
                }).catch(() => {
                    this.$router.back();
                })
            },

        },
        watch: {
            selectedReseller() {
                if (!this.isUpdateMode) {
                    this.organization.applyOrganizationTheme = false;
                }

                if (this.selectedReseller !== 0 && this.selectedReseller !== null) {
                    this.setResellerThemeData();
                }
            }
        },
    }
</script>

<style lang="scss">

    .icon-wrap {
        width: fit-content;
        height: 150px;
    }

    .logo-wrap {
        width: fit-content;
        height: 150px;
    }

    img {
        max-width: 100%;
        max-height: 100%;
    }

    .bottom-margin-30 {
        margin-bottom: 30px !important;
    }

    .form-title-margin {
        margin: 20px 0 10px 0 !important;
    }

    .checkbox-section {
        display: flex;
        flex-direction: column;
    }

</style>
